import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import axios from '../../../../config/axios';
import { InputDiv, Label, Input, SearchOptions, Option } from '../../../../components/styles/select-style';

interface ItemType {
  id: number;
}

interface CompanySelectProps<T extends ItemType> {
  label: string;
  apiUrl: string;
  displayField: keyof T;
  queryParams?: Record<string, string | number>;
  value: T;
  onChange: (item: T) => void;
}

const CompanySelect = <T extends ItemType>({
  label,
  apiUrl,
  displayField,
  queryParams = {},
  value,
  onChange,
}: CompanySelectProps<T>) => {
  console.log('🔵 [CompanySelect] Component mounted/rendered:', { label, apiUrl, queryParams, value });

  const [items, setItems] = useState<T[]>([]);
  const [allCompanies, setAllCompanies] = useState<T[]>([]);
  const [inputItem, setInputItem] = useState<string>(value[displayField] as string || '');
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const optionsRef = useRef<HTMLUListElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    setInputItem(value[displayField] as string || '');
  }, [value.id, displayField]);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        console.log('🔍 [CompanySelect] Iniciando fetch:', { label, apiUrl, queryParams });

        const response = await axios.get<any>(apiUrl, {
          params: { ...queryParams, loadQuantity: 1000 },
        });

        console.log('🔍 [CompanySelect] Response received:', {
          label,
          queryParams,
          dataType: Array.isArray(response.data) ? 'array' : typeof response.data,
          hasData: !!response.data?.data,
          itemCount: Array.isArray(response.data) ? response.data.length : response.data?.data?.length || 0,
          sample: response.data
        });

        const dataArray = Array.isArray(response.data) ? response.data : (response.data?.data || []);

        console.log('🔍 [CompanySelect] DataArray processado:', {
          label,
          isArray: Array.isArray(dataArray),
          length: dataArray?.length || 0,
          firstItem: dataArray?.[0]
        });

        if (Array.isArray(dataArray)) {
          const companies = dataArray;
          setAllCompanies(companies);
          setItems(companies);
          console.log('✅ [CompanySelect] Companies carregadas:', { label, count: companies.length });
        } else {
          console.error("❌ [CompanySelect] Resposta inválida da API, esperado um array.", response.data);
          setAllCompanies([]);
          setItems([]);
        }
      } catch (error) {
        console.error("❌ [CompanySelect] Erro ao buscar dados:", { label, error });
        setAllCompanies([]);
        setItems([]);
      }
    };
    fetchCompanies();
  }, [apiUrl, JSON.stringify(queryParams), label]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        optionsRef.current &&
        inputRef.current &&
        !optionsRef.current.contains(target) &&
        !inputRef.current.contains(target)
      ) {
        setShowOptions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    console.log('⌨️ [CompanySelect] Input changed:', { label, newValue, allCompaniesCount: allCompanies.length });
    setInputItem(newValue);

    const filterCriteria = newValue.toLowerCase();

    if (newValue === '') {
      setItems(allCompanies);
    } else {
      const filteredItems = allCompanies.filter(item =>
        (item[displayField] as string)?.toLowerCase()?.includes(filterCriteria)
      );
      setItems(filteredItems);
    }

    setSelectedIndex(0);

    onChange({ id: 0, [displayField]: newValue } as unknown as T);
    setShowOptions(true);
  };

  const handleSelectItem = (item: T) => {
    console.log('✅ [CompanySelect] Item selected:', { label, item });
    setInputItem(item[displayField] as string);
    setShowOptions(false);
    onChange(item);
  };

  const handleSetOptionByKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' && selectedIndex < items.length - 1) {
      setSelectedIndex(prev => prev + 1);
      e.preventDefault();
    }
    if (e.key === 'ArrowUp' && selectedIndex > 0) {
      setSelectedIndex(prev => prev - 1);
      e.preventDefault();
    }
    if (e.key === 'Enter') {
      if (items[selectedIndex]) {
        handleSelectItem(items[selectedIndex]);
      }
      e.preventDefault();
    }
    if (e.key === 'Tab') {
      setShowOptions(false);
    }
  };

  return (
    <InputDiv>
      <Label>{label}</Label>
      <Input
        ref={inputRef}
        type='text'
        placeholder='Selecione'
        value={inputItem}
        onFocus={() => {
          console.log('👆 [CompanySelect] Input focused:', { label, itemsCount: items.length, allCompaniesCount: allCompanies.length });
          setShowOptions(true);
        }}
        onChange={handleChangeInput}
        onKeyDown={handleSetOptionByKey}
      />
      {showOptions && items && items.length > 0 &&
        <SearchOptions ref={optionsRef}>
          {items.map((s, index) =>
            <Option
              key={s.id}
              onClick={() => handleSelectItem(s)}
              isSelected={index === selectedIndex}
            >
              {s[displayField] as React.ReactNode}
            </Option>
          )}
        </SearchOptions>
      }
    </InputDiv>
  );
};

export default CompanySelect;

